import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AddServerView.module.css";
import DottedBg2 from "../../components/originkit/ui/chromatic-waves";
import Button from "../../shared/Button";
import { hostService } from "../../features/Host/hostService";

const CODE_LENGTH = 6;
const REDIRECT_SECONDS = 5;

type Step = "code" | "name" | "done";

const AddServerView = () =>
{
	const navigate = useNavigate();
	const [step, setStep] = useState<Step>("code");
	const [name, setName] = useState<string>("");
	const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [secondsLeft, setSecondsLeft] = useState<number>(REDIRECT_SECONDS);
	const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
	const nameInputRef = useRef<HTMLInputElement | null>(null);

	const joinedCode = code.join("");

	useEffect(() =>
	{
		if (step !== "code" || joinedCode.length < CODE_LENGTH)
		{
			return;
		}

		let isCancelled = false;
		setIsSubmitting(true);
		setError(null);

		hostService.verify(joinedCode)
			.then(() =>
			{
				if (!isCancelled)
				{
					setStep("name");
				}
			})
			.catch((verifyError: unknown) =>
			{
				if (!isCancelled)
				{
					setError(verifyError instanceof Error ? verifyError.message : "Failed to verify the registration code");
					setCode(Array(CODE_LENGTH).fill(""));
					inputRefs.current[0]?.focus();
				}
			})
			.finally(() =>
			{
				if (!isCancelled)
				{
					setIsSubmitting(false);
				}
			});

		return () =>
		{
			isCancelled = true;
		};
	}, [step, joinedCode]);

	useEffect(() =>
	{
		if (step === "name")
		{
			nameInputRef.current?.focus();
		}
	}, [step]);

	useEffect(() =>
	{
		if (step !== "done")
		{
			return;
		}

		const interval = window.setInterval(() => setSecondsLeft((previousSeconds) => previousSeconds - 1), 1000);
		const timeout = window.setTimeout(() => navigate("/servers"), REDIRECT_SECONDS * 1000);

		return () =>
		{
			window.clearInterval(interval);
			window.clearTimeout(timeout);
		};
	}, [step, navigate]);

	const focusNextInput = (index: number) =>
	{
		const nextInput = inputRefs.current[index];
		if (nextInput)
		{
			nextInput.focus();
			nextInput.select();
		}
	};

	const applyCodeValue = (index: number, nextValue: string) =>
	{
		const sanitizedValue = nextValue.replace(/\s+/g, "").slice(-1).toUpperCase();

		setCode((previousCode) =>
		{
			const nextCode = [...previousCode];
			nextCode[index] = sanitizedValue;
			return nextCode;
		});

		if (sanitizedValue && index < CODE_LENGTH - 1)
		{
			focusNextInput(index + 1);
		}
	};

	const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) =>
	{
		const typedCharacter = event.key.replace(/\s+/g, "").slice(-1);

		if (event.key === "Backspace" && !code[index] && index > 0)
		{
			inputRefs.current[index - 1]?.focus();
			return;
		}

		if (typedCharacter && event.currentTarget.selectionStart === 0 && event.currentTarget.selectionEnd === 1 && !event.ctrlKey && !event.metaKey && !event.altKey)
		{
			event.preventDefault();
			applyCodeValue(index, typedCharacter);
		}
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) =>
	{
		event.preventDefault();
		const pastedValue = event.clipboardData.getData("text").replace(/\s+/g, "").slice(0, CODE_LENGTH).toUpperCase();

		if (!pastedValue)
		{
			return;
		}

		const nextCode = Array(CODE_LENGTH).fill("");
		for (let i = 0; i < pastedValue.length; i += 1)
		{
			nextCode[i] = pastedValue[i];
		}

		setCode(nextCode);
		const nextFocusIndex = Math.min(pastedValue.length, CODE_LENGTH - 1);
		inputRefs.current[nextFocusIndex]?.focus();
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) =>
	{
		event.preventDefault();

		if (name.trim().length === 0 || isSubmitting)
		{
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try
		{
			await hostService.claim(joinedCode, name.trim());
			setStep("done");
		}
		catch (claimError)
		{
			setError(claimError instanceof Error ? claimError.message : "Failed to add the server host");
		}
		finally
		{
			setIsSubmitting(false);
		}
	};

	const handleBackToCode = () =>
	{
		setError(null);
		setCode(Array(CODE_LENGTH).fill(""));
		setStep("code");
	};

	return (
		<div className={styles.addServerView}>
			<div className={styles.background} aria-hidden="true">
				<DottedBg2 bgColor="var(--color-background)" colors={["var(--color-background-bright)"]} speed={1} frequency={2} />
			</div>
			{step === "code" && (
				<div className={styles.form}>
					<h1>Add your server host</h1>
					<p>Enter the code that you get when you run the server host for the first time.</p>
					<div className={styles.codeInputWrapper} aria-label="Server host code entry">
						{Array.from({ length: CODE_LENGTH }, (_, index) => (
							<input
								key={index}
								ref={(element) =>
								{
									inputRefs.current[index] = element;
								}}
								className={styles.codeInput}
								type="text"
								autoCapitalize="characters"
								maxLength={1}
								disabled={isSubmitting}
								value={code[index]}
								onChange={(event) => applyCodeValue(index, event.target.value)}
								onKeyDown={(event) => handleKeyDown(index, event)}
								onPaste={handlePaste}
								onClick={(event) => event.currentTarget.select()}
								aria-label={`Server host code character ${index + 1}`}
							/>
						))}
					</div>
					{isSubmitting && <p className={styles.hint}>Checking the code...</p>}
					{error && <p className={styles.error} role="alert">{error}</p>}
				</div>
			)}
			{step === "name" && (
				<form className={styles.form} onSubmit={handleSubmit}>
					<h1>Name your server host</h1>
					<p>Code {joinedCode} was accepted. Choose a name so you can recognise this host later.</p>
					<input
						ref={nameInputRef}
						className={styles.nameInput}
						type="text"
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder="Server host name"
						aria-label="Server host name"
					/>
					{error && <p className={styles.error} role="alert">{error}</p>}
					<div className={styles.actions}>
						<Button
							label="Back"
							disabled={isSubmitting}
							onClick={handleBackToCode}
						/>
						<Button
							type="submit"
							label={isSubmitting ? "Adding..." : "Confirm"}
							disabled={name.trim().length === 0 || isSubmitting}
						/>
					</div>
				</form>
			)}
			{step === "done" && (
				<div className={styles.result}>
					<h1>Server host added</h1>
					<p>Returning in {secondsLeft}...</p>
				</div>
			)}
		</div>
	);
};

export default AddServerView;